import { useState, useEffect } from 'react'
import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'
import { Form, useActionData, useNavigation, useLoaderData } from 'react-router'
import type { MetaFunction, ActionFunctionArgs, LoaderFunctionArgs } from 'react-router'
import { redirect } from 'react-router'
import { db } from '@afficheservices/db'
import { professionalProfiles, professionalCategories, serviceCategories } from '@afficheservices/db/schema'
import { eq, isNull, sql } from 'drizzle-orm'
import { register, createSessionCookie } from '~/lib/auth.server'
import { slugify } from '@afficheservices/utils'
import { sendVerificationEmail } from '~/lib/email-verification.server'
import { validateSiret } from '~/lib/siret.server'
import { formatPhone, formatSiret, formatPostalCode } from '~/hooks/use-formatter'
import { sendEmail, emailTemplate } from '@afficheservices/email'
import {
  ArrowRight, ArrowLeft, User, Building2, Wrench, CheckCircle, Shield,
  Zap, TrendingUp, Star, Eye, Clock, Users, BadgeCheck, Gift,
} from 'lucide-react'

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export const meta: MetaFunction = () => [
  { title: 'Inscription Pro — Recevez des demandes de devis | AfficheServices' },
  { name: 'description', content: 'Inscrivez votre entreprise gratuitement sur AfficheServices. Recevez des demandes de devis qualifiées de particuliers près de chez vous.' },
]

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const ref = url.searchParams.get('ref') ?? ''

  const categories = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr })
    .from(serviceCategories)
    .where(isNull(serviceCategories.parentId))
    .orderBy(serviceCategories.displayOrder)

  const [proCount] = await db
    .select({ count: sql<number>`count(*)` })
    .from(professionalProfiles)

  return { categories, proCount: Number(proCount?.count ?? 0), ref }
}

export async function action({ request }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'auth')) return rateLimitResponse()
  const formData = await request.formData()

  const firstName = formData.get('firstName') as string
  const lastName = formData.get('lastName') as string
  const email = formData.get('email') as string
  const password = formData.get('password') as string
  const phone = formData.get('phone') as string
  const companyName = formData.get('companyName') as string
  const siret = (formData.get('siret') as string)?.replace(/\s/g, '')
  const legalForm = formData.get('legalForm') as string
  const postalCode = formData.get('postalCode') as string
  const city = formData.get('city') as string
  const categoryIds = formData.getAll('categoryIds') as string[]

  const errors: Record<string, string> = {}
  if (!firstName) errors.firstName = 'Requis'
  if (!lastName) errors.lastName = 'Requis'
  if (!email) errors.email = 'Requis'
  if (!password || password.length < 8) errors.password = 'Min 8 caractères'
  if (!phone) errors.phone = 'Requis'
  if (!siret || siret.length !== 14) errors.siret = 'SIRET invalide (14 chiffres)'
  if (categoryIds.length === 0) errors.categories = 'Sélectionnez au moins une catégorie'
  // companyName / postalCode / city are NOT required from the user — they are
  // pulled from the SIRENE registry below (the SIRET already supplies them).
  // A safety net after validation handles the rare incomplete-SIRENE case.

  if (Object.keys(errors).length > 0) return { errors, step: getErrorStep(errors) }

  const siretResult = await validateSiret(siret)
  if (!siretResult.valid) {
    return { errors: { siret: siretResult.error ?? 'SIRET invalide' }, step: 2 }
  }
  // Prefer SIRENE data, fall back to anything the user optionally typed.
  const finalCompanyName = siretResult.companyName || companyName
  const finalPostalCode = siretResult.postalCode || postalCode
  const finalCity = siretResult.city || city
  if (!finalCompanyName || !finalPostalCode || finalPostalCode.length !== 5 || !finalCity) {
    return { errors: { siret: 'Infos entreprise introuvables pour ce SIRET — vérifiez le numéro.' }, step: 2 }
  }

  const result = await register({ email, password, firstName, lastName, phone, role: 'professional' })
  if ('error' in result) return { errors: { form: result.error }, step: 1 }

  const siren = siret.slice(0, 9)
  const slug = slugify(`${finalCompanyName}-${finalCity}`)

  await db.insert(professionalProfiles).values({
    userId: result.userId!,
    companyName: finalCompanyName,
    siret, siren,
    siretVerified: true, siretVerifiedAt: new Date(),
    legalForm: legalForm as any || 'auto_entrepreneur',
    postalCode: finalPostalCode,
    city: finalCity,
    departmentCode: finalPostalCode.slice(0, 2),
    slug,
    identityVerified: true,
  })

  const profile = await db.select({ id: professionalProfiles.id }).from(professionalProfiles).where(eq(professionalProfiles.userId, result.userId!)).limit(1)
  if (profile[0]) {
    for (const catId of categoryIds) {
      await db.insert(professionalCategories).values({ professionalId: profile[0].id, categoryId: catId })
    }
  }

  // ── Referral tracking ──────────────────────────────────────
  const ref = formData.get('ref') as string
  if (ref && profile[0]) {
    const [referrer] = await db
      .select({ id: professionalProfiles.id, metaData: professionalProfiles.metaData })
      .from(professionalProfiles)
      .where(eq(professionalProfiles.slug, ref))
      .limit(1)
    if (referrer) {
      // Store referral on the new pro
      await db.update(professionalProfiles)
        .set({ metaData: { referredBy: referrer.id, referralCode: ref, referredAt: new Date().toISOString() }, updatedAt: new Date() })
        .where(eq(professionalProfiles.id, profile[0].id))
      // Store referral on the referrer
      const referrerMeta = (referrer.metaData as Record<string, any>) ?? {}
      const referrals = Array.isArray(referrerMeta.referrals) ? referrerMeta.referrals : []
      referrals.push({ professionalId: profile[0].id, at: new Date().toISOString() })
      await db.update(professionalProfiles)
        .set({ metaData: { ...referrerMeta, referrals }, updatedAt: new Date() })
        .where(eq(professionalProfiles.id, referrer.id))
    }
  }

  // Send verification email
  sendVerificationEmail(result.userId!, email, firstName).catch(console.error)

  sendEmail({
    to: email,
    subject: 'Bienvenue sur AfficheServices !',
    html: emailTemplate({
      title: `Bienvenue ${escapeHtml(firstName)} !`,
      body: `
        <p>Votre compte professionnel <strong>${escapeHtml(companyName)}</strong> est créé et votre SIRET vérifié.</p>
        <div style="background: #f0fdf4; border-radius: 12px; padding: 16px; margin: 20px 0;">
          <p style="margin: 0; color: #166534; font-weight: 600;">Ce qui est prêt :</p>
          <ul style="margin: 8px 0 0; padding-left: 20px; color: #166534; font-size: 14px;">
            <li>Compte créé</li>
            <li>SIRET vérifié via INSEE</li>
            <li>Identité confirmée</li>
          </ul>
        </div>
        <div style="background: #fffbeb; border-radius: 12px; padding: 16px; margin: 20px 0;">
          <p style="margin: 0; color: #92400e; font-weight: 600;">Pour recevoir vos premiers leads :</p>
          <ul style="margin: 8px 0 0; padding-left: 20px; color: #92400e; font-size: 14px;">
            <li>Ajoutez une description de votre entreprise</li>
            <li>Indiquez vos années d'expérience</li>
            <li>Définissez votre zone d'intervention</li>
          </ul>
        </div>
        <div style="text-align: center; margin: 24px 0;">
          <a href="${process.env.APP_URL ?? 'https://www.afficheservices.fr'}/espace-pro/profil" style="display: inline-block; padding: 12px 28px; background: #2563eb; color: white; text-decoration: none; border-radius: 10px; font-weight: 600; font-size: 14px;">
            Compléter mon profil
          </a>
        </div>
      `,
      footerText: 'Vous recevez 2 leads gratuits par mois. Passez à un plan payant pour en recevoir plus.',
    }),
  }).catch(console.error)

  const cookie = await createSessionCookie(result.userId!, request)
  return redirect('/espace-pro', { headers: { 'Set-Cookie': cookie } })
}

function getErrorStep(errors: Record<string, string>): number {
  if (errors.firstName || errors.lastName || errors.email || errors.password || errors.phone) return 1
  if (errors.companyName || errors.siret || errors.postalCode || errors.city) return 2
  return 3
}

const STEPS = [
  { label: 'Compte', icon: User },
  { label: 'Entreprise', icon: Building2 },
  { label: 'Services', icon: Wrench },
]

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'Plomberie': Wrench, 'Électricité': Zap, 'Peinture': Star,
  'Rénovation': Building2, 'Menuiserie': Wrench, 'Toiture': Building2,
  'Serrurerie': Shield, 'Climatisation': Zap, 'Nettoyage': Star,
  'Jardinage': Star, 'Déménagement': Users, 'Carrelage': Wrench,
}

const INPUT_CLS = 'block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
const ERROR_CLS = 'mt-1 text-xs text-red-500'

export default function RegisterProPage() {
  const { categories, proCount, ref } = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const isSubmitting = navigation.state === 'submitting'
  const errors = actionData?.errors as Record<string, string> | undefined

  const [step, setStep] = useState(actionData?.step ?? 1)
  const [phone, setPhone] = useState('')
  const [siret, setSiret] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  const [password, setPassword] = useState('')
  const [stepErrors, setStepErrors] = useState<string[]>([])
  const [selectedCats, setSelectedCats] = useState<string[]>([])

  const strength = password.length >= 12 ? 'Fort' : password.length >= 8 ? 'Moyen' : password.length > 0 ? 'Faible' : ''

  // Auto-detect city from postal code
  useEffect(() => {
    if (postalCode.length !== 5) { setCity(''); return }
    fetch(`https://api-adresse.data.gouv.fr/search/?q=${postalCode}&type=municipality&limit=1`)
      .then(r => r.json())
      .then(data => {
        if (data.features?.length > 0) {
          setCity(data.features[0].properties.city ?? data.features[0].properties.label ?? '')
        }
      })
      .catch(() => {})
  }, [postalCode])

  // Client-side step validation
  function validateStep(currentStep: number): boolean {
    const errs: string[] = []
    if (currentStep === 1) {
      const form = document.querySelector('form')
      const firstName = (form?.querySelector('[name="firstName"]') as HTMLInputElement)?.value
      const lastName = (form?.querySelector('[name="lastName"]') as HTMLInputElement)?.value
      const email = (form?.querySelector('[name="email"]') as HTMLInputElement)?.value
      if (!firstName?.trim()) errs.push('Prénom requis')
      if (!lastName?.trim()) errs.push('Nom requis')
      if (!email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.push('Email invalide')
      if (password.length < 8) errs.push('Mot de passe min 8 caractères')
      if (!phone || phone.replace(/\s/g, '').length < 10) errs.push('Téléphone invalide')
    }
    if (currentStep === 2) {
      // Only the SIRET is required — raison sociale / code postal / ville are
      // pulled from SIRENE on submit (the fields below are optional fallbacks).
      if (!siret || siret.replace(/\s/g, '').length !== 14) errs.push('SIRET invalide (14 chiffres)')
    }
    setStepErrors(errs)
    return errs.length === 0
  }

  function nextStep() {
    if (validateStep(step)) {
      setStep(step + 1)
      setStepErrors([])
    }
  }

  function toggleCategory(id: string) {
    setSelectedCats(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : prev.length < 5 ? [...prev, id] : prev
    )
  }

  return (
    <div>
      {/* Pro-specific header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <BadgeCheck className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Inscription Professionnel</h1>
          <p className="text-xs text-slate-500">Rejoignez {proCount > 0 ? `${proCount}+` : 'nos'} artisans vérifiés</p>
        </div>
      </div>

      {/* Benefits strip */}
      <div className="mb-6 flex flex-wrap gap-2">
        {[
          { icon: Gift, text: '2 leads gratuits/mois', color: 'bg-emerald-50 text-emerald-700' },
          { icon: Eye, text: 'Profil visible', color: 'bg-blue-50 text-blue-700' },
          { icon: Clock, text: 'Inscription 2 min', color: 'bg-amber-50 text-amber-700' },
        ].map((b) => (
          <span key={b.text} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ${b.color}`}>
            <b.icon className="h-3 w-3" /> {b.text}
          </span>
        ))}
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const StepIcon = s.icon
          const isActive = step === i + 1
          const isDone = step > i + 1
          return (
            <button
              key={s.label}
              type="button"
              onClick={() => { if (isDone) { setStep(i + 1); setStepErrors([]) } }}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold transition-all ${
                isActive ? 'bg-primary text-white shadow-sm' :
                isDone ? 'bg-emerald-50 text-emerald-700 cursor-pointer hover:bg-emerald-100' :
                'bg-slate-100 text-slate-400'
              }`}
            >
              {isDone ? <CheckCircle className="h-3.5 w-3.5" /> : <StepIcon className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{i + 1}</span>
            </button>
          )
        })}
      </div>

      {/* Client-side validation errors */}
      {stepErrors.length > 0 && (
        <div className="mt-3 rounded-xl bg-red-50 px-4 py-2.5">
          {stepErrors.map((err, i) => (
            <p key={i} className="text-xs text-red-600">{err}</p>
          ))}
        </div>
      )}

      <Form method="post" className="mt-5">
        <input type="hidden" name="ref" value={ref} />
        {errors?.form && (
          <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{errors.form}</div>
        )}

        {/* ── Step 1: Account ──────────────────────── */}
        <div className={step === 1 ? 'space-y-3' : 'hidden'}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className="block text-xs font-medium text-slate-600">Prénom *</label>
              <input id="firstName" name="firstName" type="text" required placeholder="Jean"
                autoComplete="given-name" className={INPUT_CLS} />
              {errors?.firstName && <p className={ERROR_CLS}>{errors.firstName}</p>}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-xs font-medium text-slate-600">Nom *</label>
              <input id="lastName" name="lastName" type="text" required placeholder="Dupont"
                autoComplete="family-name" className={INPUT_CLS} />
            </div>
          </div>
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-slate-600">Email professionnel *</label>
            <input id="email" name="email" type="email" required placeholder="jean@entreprise.fr"
              autoComplete="email" className={INPUT_CLS} />
            {errors?.email && <p className={ERROR_CLS}>{errors.email}</p>}
          </div>
          <div>
            <label htmlFor="phone" className="block text-xs font-medium text-slate-600">Téléphone *</label>
            <input id="phone" name="phone" type="tel" required placeholder="06 12 34 56 78"
              autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))}
              className={INPUT_CLS} />
            <p className="mt-0.5 text-[9px] text-slate-300">Les clients vous contacteront sur ce numéro</p>
          </div>
          <div>
            <label htmlFor="password" className="block text-xs font-medium text-slate-600">Mot de passe *</label>
            <input id="password" name="password" type="password" required minLength={8}
              placeholder="Minimum 8 caractères" autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className={INPUT_CLS} />
            {password.length > 0 && (
              <div className="mt-1.5 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full transition-all duration-300 ${
                    password.length >= 12 ? 'w-full bg-emerald-500' :
                    password.length >= 8 ? 'w-2/3 bg-amber-500' : 'w-1/3 bg-red-400'
                  }`} />
                </div>
                <span className={`text-[11px] font-medium ${
                  password.length >= 12 ? 'text-emerald-600' : password.length >= 8 ? 'text-amber-600' : 'text-red-500'
                }`}>{strength}</span>
              </div>
            )}
            {errors?.password && <p className={ERROR_CLS}>{errors.password}</p>}
          </div>

          <button type="button" onClick={nextStep}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press">
            Suivant <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* ── Step 2: Company ──────────────────────── */}
        <div className={step === 2 ? 'space-y-3' : 'hidden'}>
          <p className="text-sm text-slate-600">
            Saisissez votre <strong>SIRET</strong> : votre raison sociale et votre adresse
            sont récupérées automatiquement auprès de l'INSEE — rien d'autre à taper.
          </p>
          <div>
            <label htmlFor="siret" className="block text-xs font-medium text-slate-600">Numéro SIRET *</label>
            <input id="siret" name="siret" type="text" required maxLength={17}
              placeholder="123 456 789 00012" inputMode="numeric"
              value={siret} onChange={(e) => setSiret(formatSiret(e.target.value))}
              className={`${INPUT_CLS} font-mono tracking-wider`} />
            <div className="mt-1 flex items-center justify-between">
              {errors?.siret ? (
                <p className={ERROR_CLS}>{errors.siret}</p>
              ) : (
                <p className="flex items-center gap-1 text-[10px] text-slate-400">
                  <Shield className="h-3 w-3 text-emerald-500" /> Vérifié via le registre SIRENE de l'INSEE
                </p>
              )}
              <span className={`text-[10px] font-medium ${siret.replace(/\s/g, '').length === 14 ? 'text-emerald-500' : 'text-slate-400'}`}>
                {siret.replace(/\s/g, '').length}/14
              </span>
            </div>
            <a href="https://annuaire-entreprises.data.gouv.fr" target="_blank" rel="noopener noreferrer"
              className="mt-1.5 inline-block text-[11px] font-medium text-primary hover:underline">
              Je n'ai pas mon SIRET sous la main →
            </a>
          </div>

          <details className="rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5">
            <summary className="cursor-pointer text-xs font-medium text-slate-500">
              Détails entreprise — facultatif (remplis via votre SIRET)
            </summary>
            <div className="mt-3 space-y-3">
              <div>
                <label htmlFor="legalForm" className="block text-xs font-medium text-slate-600">Forme juridique</label>
                <select id="legalForm" name="legalForm" className={INPUT_CLS}>
                  <option value="auto_entrepreneur">Auto-entrepreneur</option>
                  <option value="eurl">EURL</option>
                  <option value="sarl">SARL</option>
                  <option value="sas">SAS</option>
                  <option value="sasu">SASU</option>
                  <option value="ei">Entreprise Individuelle</option>
                </select>
              </div>
              <div>
                <label htmlFor="companyName" className="block text-xs font-medium text-slate-600">Nom de l'entreprise</label>
                <input id="companyName" name="companyName" type="text" placeholder="Rempli via le SIRET" className={INPUT_CLS} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="postalCode" className="block text-xs font-medium text-slate-600">Code postal</label>
                  <input id="postalCode" name="postalCode" type="text" maxLength={5}
                    placeholder="Rempli via le SIRET" inputMode="numeric" value={postalCode}
                    onChange={(e) => setPostalCode(formatPostalCode(e.target.value))}
                    className={INPUT_CLS} />
                </div>
                <div>
                  <label htmlFor="city" className="block text-xs font-medium text-slate-600">Ville</label>
                  <input id="city" name="city" type="text" placeholder="Rempli via le SIRET"
                    value={city} onChange={(e) => setCity(e.target.value)}
                    className={INPUT_CLS} />
                </div>
              </div>
            </div>
          </details>

          <div className="mt-2 flex gap-3">
            <button type="button" onClick={() => { setStep(1); setStepErrors([]) }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
            <button type="button" onClick={nextStep}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
              Suivant <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Step 3: Categories ───────────────────── */}
        <div className={step === 3 ? 'space-y-4' : 'hidden'}>
          <div>
            <p className="text-sm font-bold text-slate-900">Quels services proposez-vous ?</p>
            <p className="mt-1 text-xs text-slate-400">
              Sélectionnez 1 à 5 spécialités · <span className="font-semibold text-primary">{selectedCats.length}/5 sélectionnées</span>
            </p>
          </div>
          {errors?.categories && <p className={ERROR_CLS}>{errors.categories}</p>}
          <div className="grid grid-cols-2 gap-2">
            {categories.map((cat) => {
              const isSelected = selectedCats.includes(cat.id)
              const CatIcon = CATEGORY_ICONS[cat.name] ?? Wrench
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => toggleCategory(cat.id)}
                  className={`flex items-center gap-2.5 rounded-xl border-2 px-3.5 py-3 text-left text-sm transition-all active:scale-[0.97] ${
                    isSelected
                      ? 'border-primary bg-primary-50 text-primary font-semibold'
                      : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                    isSelected ? 'bg-primary text-white' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {isSelected ? <CheckCircle className="h-3.5 w-3.5" /> : <CatIcon className="h-3.5 w-3.5" />}
                  </div>
                  {cat.name}
                </button>
              )
            })}
          </div>
          {/* Hidden inputs for form submission */}
          {selectedCats.map(id => (
            <input key={id} type="hidden" name="categoryIds" value={id} />
          ))}

          {/* What happens next */}
          <div className="rounded-xl bg-slate-50 p-3.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Après inscription</p>
            <div className="mt-2 space-y-1.5">
              {[
                { icon: CheckCircle, text: 'SIRET vérifié automatiquement', color: 'text-emerald-500' },
                { icon: Eye, text: 'Profil visible sur AfficheServices', color: 'text-blue-500' },
                { icon: Zap, text: '2 leads gratuits chaque mois', color: 'text-amber-500' },
                { icon: TrendingUp, text: 'Recevez des demandes de devis qualifiées', color: 'text-primary' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-xs text-slate-600">
                  <item.icon className={`h-3.5 w-3.5 ${item.color}`} />
                  {item.text}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => { setStep(2); setStepErrors([]) }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
            <button type="submit" disabled={isSubmitting || selectedCats.length === 0}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-bold text-white shadow-sm hover:bg-primary-hover disabled:opacity-50 btn-press">
              {isSubmitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Création...
                </>
              ) : (
                <>Créer mon compte Pro <CheckCircle className="h-4 w-4" /></>
              )}
            </button>
          </div>

          {/* Trust signal near submit */}
          <p className="text-center text-[10px] text-slate-400">
            <Shield className="mr-1 inline h-3 w-3 text-emerald-500" />
            Inscription gratuite · Sans engagement · Sans carte bancaire
          </p>
        </div>
      </Form>

      <p className="mt-6 text-center text-xs text-slate-500">
        Déjà inscrit ? <a href="/login" className="font-medium text-primary hover:underline">Se connecter</a>
      </p>
    </div>
  )
}
